import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect, useDispatch } from 'react-redux';
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { AGREEMENTS } from 'CorePackage/MainModule/routes/routes';
import {
  AGREEMENTS_BLOCK_ID,
  getAgreementsBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef, getKnmHistoryChanges } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { IKnmPmFormResultDecision } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormResultDecision';
import { KnmResultDecisionStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionStatusEnum';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmGetHistoryChanges, knmSetHistoryChangesIsOn } from 'KnmPackage/MainModule/actions/knmActions';
import { IKnmHistoryChanges } from 'KnmPackage/MainModule/interfaces/IKnmHistoryChanges';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { onApprovalSelectOption } from 'KnmErknmPackage/MainModule/constants/approvedSelectOptions';
import { IDecisionOrganizationForm } from 'DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { KnmResponsibleTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResponsibleTypeCodesEnum';
import { getDirtyResultDecisionGuid } from 'CorePackage/HeaderModule/reducers/headerReducer';

import { ErknmAgreementsItem } from './ErknmAgreementsItem';
import { AgreementsContext } from './AgreementsContext';
import { IGeneralResultDecision } from './interfaces/IGeneralResultDecision';

interface IErknmAgreementsProps {
  resultDecisionsKnm?: IKnmErknmFormResultDecision[] | undefined;
  resultDecisionsPm?: IKnmPmFormResultDecision[] | undefined;
  decisionOrganization?: IDecisionOrganizationForm | undefined;
  agreements: IErknmFormAgreement[] | undefined;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
}

interface IErknmAgreementsStoreProps {
  formRef: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
  knmHistoryChanges: IKnmHistoryChanges | undefined;
  IsProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  dirtyResultDecisionGuid: string | undefined;
}

const ErknmAgreementsWithoutStore = ({
  formRef,
  decisionFormRef,
  resultDecisionsKnm,
  resultDecisionsPm,
  decisionOrganization,
  agreements,
  knmHistoryChanges,
  isKnm,
  isPm,
  isDecision,
  IsProsecutorOrSuperProsecutor,
  isSupervisor,
  dirtyResultDecisionGuid,
}: IErknmAgreementsProps & IErknmAgreementsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = isDecision ? decisionFormRef?.current.mutators.push : formRef?.current.mutators.push;
  const remove = isDecision ? decisionFormRef?.current.mutators.remove : formRef?.current.mutators.remove;
  const [editedAgreementGuid, setEditedAgreementGuid] = useState('');

  /**
   * Запрос на получение списка причин изменения соглашения
   */
  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.AGREEMENT_CHANGE_REASON));
  }, []);

  /**
   * Определяем, есть ли соглашения, которые ранее были уже в паспорте
   * и которые уже были отправлены на согласование
   */
  useEffect(() => {
    if (IsProsecutorOrSuperProsecutor && agreements && !knmHistoryChanges) {
      const agreementsOnApprovingOrAfter = agreements.filter(a => a.status !== AgreementStatusEnum.DRAFT);

      if (agreementsOnApprovingOrAfter.length) {
        dispatch(knmSetHistoryChangesIsOn(true));
        dispatch(knmGetHistoryChanges());
      }
    }
  }, [agreements, IsProsecutorOrSuperProsecutor, knmHistoryChanges]);

  const resultDecisions = useMemo(() => {
    const items: IGeneralResultDecision[] = [];

    if (resultDecisionsKnm) {
      resultDecisionsKnm.forEach(rd =>
        items.push({
          guid: rd.guid || '',
          kindDecision: rd.kindDecision,
          number: rd.numberDecision,
          organization: rd.organization,
          status: rd.status,
          signed: rd.signed,
          violatedMandatoryRequirements: rd.violatedMandatoryRequirements,
          date: rd.dateDecision,
        }),
      );
    }

    if (resultDecisionsPm) {
      resultDecisionsPm.forEach(rd =>
        items.push({
          guid: rd.guid || '',
          kindDecision: rd.kindDecision,
          number: rd.number,
          // @ts-ignore
          organization: {
            ...rd.organization,
            addressFns: '',
            isAutocomplete: false,
            guid: '',
            isDocumentOrganization: false,
            listId: '',
            subjectType: rd.organization?.subjectTypePm,
            isForeignSubjectAgencyNotRegistredInRf: undefined,
            epguNotifications: [],
          },
          status: rd.status,
          signed: rd.signed,
          violatedMandatoryRequirements: rd.violatedMandatoryRequirements,
          date: rd.date,
        }),
      );
    }

    if (decisionOrganization?.resultDecisions?.length) {
      decisionOrganization?.resultDecisions.forEach(rd =>
        items.push({
          guid: rd.guid || '',
          kindDecision: rd.kind,
          number: rd.number,
          // @ts-ignore
          organization: {
            ...decisionOrganization,
            addressFns: '',
            isAutocomplete: false,
            guid: '',
            isDocumentOrganization: false,
            listId: '',
            subjectType: decisionOrganization.organizationType,
            isForeignSubjectAgencyNotRegistredInRf: undefined,
            epguNotifications: [],
          },
          status: rd.status,
          signed: rd.signed,
          violatedMandatoryRequirements: rd.eliminatedMandatoryRequirements,
          date: rd.date,
        }),
      );
    }

    return items;
  }, [resultDecisionsKnm, resultDecisionsPm, decisionOrganization]);

  const makeRemoveAgreementHandler = (index: number, guid: string) => () => {
    if (guid === editedAgreementGuid) {
      setEditedAgreementGuid('');
    }
    remove && remove('agreements', index);
  };

  const addAgreement = useCallback(() => {
    if (push) {
      const id = uuidv4();

      push('agreements', {
        guid: id,
        listId: id,
        isNew: true,
        status: AgreementStatusEnum.DRAFT,
        approveInfo: {
          approved: onApprovalSelectOption,
        },
      } as IErknmFormAgreement);
    }
  }, [push]);

  const isDisabledButtonAdd = useMemo(() => {
    const prescriptions = resultDecisions?.filter(
      rd =>
        (rd.kindDecision?.code === KindResultDecisionCodesEnum.D_1 ||
          rd.kindDecision?.code === KindResultDecisionCodesEnum.D_7) &&
        rd.signed &&
        rd.status === KnmResultDecisionStatusEnum.NOT_COMPLETED &&
        (rd.organization?.subjectType?.code === KnmResponsibleTypeCodesEnum.UR ||
          rd.organization?.subjectType?.code === KnmResponsibleTypeCodesEnum.IUR ||
          rd.organization?.subjectType?.code === KnmResponsibleTypeCodesEnum.FIUR),
    );

    const newAgreement = agreements?.find(i => i.isNew);
    const availableAgreements = agreements?.filter(
      i =>
        i.status === AgreementStatusEnum.DRAFT ||
        i.status === AgreementStatusEnum.READY_TO_APPROVE ||
        i.status === AgreementStatusEnum.ON_APPROVAL ||
        i.status === AgreementStatusEnum.APPROVED,
    );

    return (
      !isSupervisor ||
      prescriptions?.length === 0 ||
      resultDecisions?.length === 0 ||
      !!editedAgreementGuid ||
      !!newAgreement ||
      prescriptions.length === availableAgreements?.length ||
      !!dirtyResultDecisionGuid
    );
  }, [resultDecisions, agreements, editedAgreementGuid, isSupervisor, dirtyResultDecisionGuid]);

  return (
    <Section
      title="Сведения о соглашениях о надлежащем устранении нарушений ОТ"
      type="list"
      id={AGREEMENTS}
      titleId={AGREEMENTS_BLOCK_ID}
      customButton={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          onClick={addAgreement}
          disabled={isDisabledButtonAdd}
        />
      }
    >
      <FieldArray name="agreements">
        {({ fields }: FieldArrayRenderProps<IErknmFormAgreement, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: IErknmFormAgreement = fields.value?.[index];
            const id = getAgreementsBlockId(fieldValue.guid || '');

            return (
              <AgreementsContext.Provider key={id} value={{ isKnm, isPm, isDecision, agreement: fieldValue }}>
                <ErknmAgreementsItem
                  resultDecisions={resultDecisions}
                  id={id}
                  name={name}
                  onRemove={makeRemoveAgreementHandler(index, fieldValue.guid || '')}
                  agreements={fields.value}
                  agreement={fieldValue}
                  agreementIndex={index}
                  editedAgreementGuid={editedAgreementGuid}
                  setEditedAgreementGuid={setEditedAgreementGuid}
                />
              </AgreementsContext.Provider>
            );
          });
        }}
      </FieldArray>
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IErknmAgreementsStoreProps => ({
  formRef: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
  knmHistoryChanges: getKnmHistoryChanges(state),
  IsProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  dirtyResultDecisionGuid: getDirtyResultDecisionGuid(state),
});

export const ErknmAgreements = connect(mapStateToProps)(ErknmAgreementsWithoutStore);
